import { Box, Card, CardContent, Chip, Divider, Stack, Typography } from "@mui/material";
import { DELIVERY_CARRIER_LABELS } from "../../../utility/constants";

// Admin view of Order.changeLog: the append-only history of post-creation
// edits to an order's notes and estimated/preferred times. Values set at
// checkout are the baseline and aren't logged. Entries made after the buyer
// claimed payment or after a dispute was opened are flagged, since those are
// the edits most likely to be an attempt to rewrite what was agreed.

const DATE_FIELDS = new Set(["ESTIMATED_READY_AT", "PREFERRED_DELIVERY_TIME"]);

// A DELIVERY_TRACKING value is an object; show one line per field.
const formatTracking = (tracking) =>
  tracking
    ? [
        tracking.carrier &&
          `Courier: ${
            tracking.carrier === "OTHER"
              ? tracking.carrierName || "Other"
              : DELIVERY_CARRIER_LABELS[tracking.carrier] || tracking.carrier
          }`,
        tracking.trackingNumber && `Tracking number: ${tracking.trackingNumber}`,
        tracking.trackingUrl && `Link: ${tracking.trackingUrl}`,
        tracking.driverName && `Driver: ${tracking.driverName}`,
        tracking.driverPhone && `Driver phone: ${tracking.driverPhone}`,
      ]
        .filter(Boolean)
        .join("\n")
    : null;

const formatDateTime = (date) =>
  date ? new Date(date).toLocaleString(undefined, { timeZoneName: "short" }) : null;

const fieldLabel = (entry, order) => {
  switch (entry.field) {
    case "SELLER_NOTE":
      return "Seller note";
    case "CUSTOMER_NOTE":
      return "Buyer note";
    case "ITEM_NOTE": {
      const item = order.items?.find(
        (i) => String(i.product?._id ?? i.product) === String(entry.product),
      );
      return `Item note: ${item?.productName || "unknown item"}`;
    }
    case "ESTIMATED_READY_AT":
      return "Estimated ready time";
    case "PREFERRED_DELIVERY_TIME":
      return "Preferred time";
    case "DELIVERY_TRACKING":
      return "Delivery tracking";
    case "DELIVERY_UPDATE":
      return "Delivery update posted";
    default:
      return entry.field;
  }
};

const ValueText = ({ field, value, muted }) => {
  const text = DATE_FIELDS.has(field)
    ? formatDateTime(value)
    : field === "DELIVERY_TRACKING"
      ? formatTracking(value)
      : value;
  return (
    <Typography
      variant="body2"
      component="span"
      color={muted || !text ? "text.secondary" : "text.primary"}
      sx={{
        fontStyle: text ? "normal" : "italic",
        textDecoration: muted && text ? "line-through" : "none",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {text || "(empty)"}
    </Typography>
  );
};

const OrderChangeLog = ({ order }) => {
  const entries = [...(order.changeLog || [])].reverse();
  const buyerName = order.user?.name || "Buyer";
  const sellerName = order.shop?.shopName || "Seller";
  const paymentClaimedAt = order.payment?.buyerClaimedAt
    ? new Date(order.payment.buyerClaimedAt)
    : null;
  const disputeOpenedAt = order.dispute?.createdAt ? new Date(order.dispute.createdAt) : null;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Typography fontWeight={600} mb={0.5}>
          Change Log
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
          Edits to notes, times and delivery tracking after the order was placed, newest first.
        </Typography>
        {entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No edits since the order was placed.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} spacing={1.5}>
            {entries.map((entry, i) => {
              const at = entry.at ? new Date(entry.at) : null;
              const afterDispute = disputeOpenedAt && at && at > disputeOpenedAt;
              const afterPaymentClaim = !afterDispute && paymentClaimedAt && at && at > paymentClaimedAt;
              return (
                <Box key={i}>
                  <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={0.5}>
                    <Chip
                      size="small"
                      label={entry.role === "SELLER" ? `Seller · ${sellerName}` : `Buyer · ${buyerName}`}
                      color={entry.role === "SELLER" ? "primary" : "secondary"}
                      variant="outlined"
                    />
                    <Typography variant="body2" fontWeight={600}>
                      {fieldLabel(entry, order)}
                    </Typography>
                    {afterDispute && <Chip size="small" color="error" label="After dispute opened" />}
                    {afterPaymentClaim && <Chip size="small" color="warning" label="After payment claimed" />}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                      {formatDateTime(entry.at)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    From: <ValueText field={entry.field} value={entry.from} muted />
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    To: <ValueText field={entry.field} value={entry.to} />
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default OrderChangeLog;
